import React, { useRef, useEffect } from 'react';
import { X, MapPin, Calendar, Languages, Shield, CheckCircle, Moon, Music, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Guide {
  id: number;
  name: string;
  image: string;
  location: string;
  experience: number;
  languages: string[];
  specialties: string[];
  bio: string;
  availability: string[];
  verified: boolean;
}

interface NightCrawlingGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NightCrawlingGuideModal: React.FC<NightCrawlingGuideModalProps> = ({ isOpen, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const guides: Guide[] = [
    {
      id: 1,
      name: 'Emmy Frass',
      image: 'https://imgur.com/tLBau6N.jpg',
      location: 'Kampala',
      experience: 5,
      languages: ['English', 'Luganda', 'Swahili'],
      specialties: ['Kampala Night Crawling', 'Nightlife Guide', 'Club VIP Access'],
      bio: 'Expert nightlife guide specializing in Kampala night crawling experiences, club tours, and the best entertainment spots in the city. Emmy knows all the hottest venues and can get you VIP access to the most exclusive clubs.',
      availability: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      verified: true
    },
    {
      id: 2,
      name: 'Azel John Makumbi',
      image: 'https://imgur.com/YTHr1B6.jpg',
      location: 'Kampala',
      experience: 4,
      languages: ['English', 'Luganda'],
      specialties: ['Kampala City Tours', 'Kampala Night Life', 'Underground Scene'],
      bio: 'Specialized in Kampala city tours and nightlife experiences, showing visitors the vibrant urban culture of Uganda\'s capital. Azel has insider knowledge of the underground scene and exclusive venues that most tourists never discover.',
      availability: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      verified: true
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-2xl"
            ref={modalRef}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center">
                <Moon className="h-5 w-5 text-purple-600 mr-2" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Nightlife Guides</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-4 max-h-[calc(90vh-4rem)]">
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Our certified nightlife guides are experts in Uganda's vibrant club scene. They'll ensure you have a safe, memorable experience with VIP access to the best venues.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {guides.map((guide) => (
                  <div key={guide.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="relative h-64">
                      <img 
                        src={guide.image}
                        alt={guide.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                      
                      {guide.verified && (
                        <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full flex items-center">
                          <Shield className="h-3 w-3 mr-1" />
                          Verified Guide
                        </div>
                      )}
                      
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-xl font-bold text-white mb-1">{guide.name}</h3>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {guide.specialties.map((specialty, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <MapPin className="h-4 w-4 text-purple-600 mr-2" />
                          {guide.location}
                        </div>
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <Calendar className="h-4 w-4 text-purple-600 mr-2" />
                          {guide.experience} years experience
                        </div>
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <Languages className="h-4 w-4 text-purple-600 mr-2" />
                          {guide.languages.join(', ')}
                        </div>
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-300 mb-4">{guide.bio}</p>
                      
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mb-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Availability</h4>
                        <div className="flex flex-wrap gap-2">
                          {guide.availability.map((day, index) => (
                            <span 
                              key={index}
                              className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs"
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <button className="flex items-center text-purple-600 hover:text-purple-800 transition-colors">
                          <Heart className="h-4 w-4 mr-1" />
                          <span className="text-sm">Save</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Why Choose Our Nightlife Guides</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300">All guides are verified and background-checked</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300">Insider access to exclusive venues</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300">24/7 emergency support during your night out</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-300">Personalized experience based on your preferences</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NightCrawlingGuideModal;